import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = article
    ? articles
        .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
        .slice(0, 3)
    : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <h1 className="text-2xl font-bold text-foreground-900">文章不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料不打烊',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://heiliaobudaying.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliaobudaying.com' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://heiliaobudaying.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <article className="w-full px-4 md:px-6 py-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <header className="mt-6 mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap">
                    {article.category}
                  </span>
                  {article.trending && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap flex items-center gap-1">
                      <i className="ri-fire-line text-[10px]"></i>
                      热门爆料
                    </span>
                  )}
                </div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {(article.views / 10000).toFixed(1)}万阅读
                  </span>
                </div>
              </header>

              <figure className="mb-8 -mx-4 md:mx-0">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`黑料不打烊 ${article.category} ${article.title}`}
                  className="w-full h-auto max-h-[500px] object-cover rounded-none md:rounded-xl"
                />
              </figure>

              <div
                className="prose prose-lg max-w-none text-foreground-800 leading-relaxed
                  prose-p:my-4 prose-p:leading-loose prose-p:text-foreground-800
                  prose-strong:text-foreground-950 prose-strong:font-semibold
                  [&_p]:text-base [&_p]:md:text-[17px]"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-8 pt-6 border-t border-background-200/70">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-sm whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-lg font-bold font-heading text-foreground-900 mb-6 flex items-center gap-2">
                  <i className="ri-links-line text-primary-500"></i>
                  相关爆料
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 no-underline"
                    >
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          title={`黑料不打烊 ${related.category} ${related.title}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold font-heading text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug mb-2">
                          {related.title}
                        </h3>
                        <span className="text-xs text-foreground-500">{related.date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}